import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { defineMailTemplate } from '@adornis/mails/entities/mail-template-definition.js';
import { assembleMailTemplate, MailTemplate } from '@adornis/mails/entities/mail-template.js';
import { Recipient } from '@adornis/mails/entities/Recipient.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { getPasswordResetLink } from '@adornis/users/server/password.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html } from 'lit';
import { concatMap, firstValueFrom, from, map, of, switchMap } from 'rxjs';
import { Booking } from '../../bookings/entities/Booking.js';
import { SGRoutes } from '../../db/enums/routes.js';
import { resolveSubscribeGroupParticipantRowPaginatedFiltered } from '../../groups/details/server/resolvers/resolveSubscribeGroupParticipantRowPaginatedFiltered.js';
import type { Kostenstelle, KostenstelleSubGroup } from '../../groups/entities/Kostenstelle.js';
import { type SchoolSubGroup } from '../../groups/entities/School.js';
import { martinusMailFooter } from '../../mails/MartinusMailFooter.js';
import { getSchoolIDsForRoles } from '../../permissions/api/getSchoolIDsForRoles.js';
import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { ACTIVE_ROLES, Permission } from '../../permissions/roles.js';
import { makeMartinusEmailTemplate } from '../../server/mailer.js';
import { getMartinusMailer } from '@martinus/mails/server/initMartinusMailer.js';
import { Person } from '../../users/entities/person.js';
import { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';
import { resolveGetUsersByListSelection } from '../../users/list/server/resolvers/resolveGetUsersByListSelection.js';

export enum FreeTextMailVariables {
  TITEL = 'titel',
  VORNAME = 'vorname',
  NACHNAME = 'nachname',
  EMAIL = 'email',
  GEBURTSDATUM = 'geburtsdatum',
  GESCHLECHT = 'geschlecht',
  STRASSE = 'strasse',
  PLZ = 'plz',
  ORT = 'ort',
  LAND = 'land',
  SET_PASSWORD_LINK = 'setPasswordLink',
  BUCHUNGS_REFERENZ = 'Ueberweiser_Verwendungszweck',
  PORTAL_LINK = 'portalLink',
}

defineMailTemplate('free-text', {
  variables: Object.values(FreeTextMailVariables),
  attachments: [],
  displayName: 'Martinus Freitext-E-Mail',
  editorHint: () => html`<span>Free Text</span>`,
  // only null coalescing is used with empty string fallback
  // eslint-disable-next-line complexity
  getMailData: (
    person: {
      email: string;
      profilDaten: Maybe<{
        stammdaten: Maybe<{
          titel: Maybe<string>;
          vorname: Maybe<string>;
          nachname: Maybe<string>;
          geburtsDatum: Maybe<Date>;
          geschlecht: Maybe<string>;
        }>;
        adresse: Maybe<{
          streetAndNumber: Maybe<string>;
          zip: Maybe<string>;
          city: Maybe<string>;
          country: Maybe<string>;
        }>;
      }>;
    },
    resetLink: string,
    bookingReference?: string,
  ) =>
    Promise.resolve({
      data: {
        [FreeTextMailVariables.TITEL]: person.profilDaten?.stammdaten?.titel ?? '',
        [FreeTextMailVariables.VORNAME]: person.profilDaten?.stammdaten?.vorname ?? '',
        [FreeTextMailVariables.NACHNAME]: person.profilDaten?.stammdaten?.nachname ?? '',
        [FreeTextMailVariables.EMAIL]: person.email,
        [FreeTextMailVariables.GEBURTSDATUM]: person.profilDaten?.stammdaten?.geburtsDatum
          ? formatCalendarDay(person.profilDaten.stammdaten.geburtsDatum, 'dd.MM.yyyy')
          : '',
        [FreeTextMailVariables.GESCHLECHT]: person.profilDaten?.stammdaten?.geschlecht ?? '',
        [FreeTextMailVariables.STRASSE]: person.profilDaten?.adresse?.streetAndNumber ?? '',
        [FreeTextMailVariables.PLZ]: person.profilDaten?.adresse?.zip ?? '',
        [FreeTextMailVariables.ORT]: person.profilDaten?.adresse?.city ?? '',
        [FreeTextMailVariables.LAND]: person.profilDaten?.adresse?.country ?? '',
        [FreeTextMailVariables.PORTAL_LINK]: `<a href="${A.absoluteUrl()}"> ${A.absoluteUrl()} </a>`,
        [FreeTextMailVariables.SET_PASSWORD_LINK]: `<a href="${resetLink}"> ${resetLink} </a>`,
        [FreeTextMailVariables.BUCHUNGS_REFERENZ]: bookingReference ?? '',
      },
      attachments: {},
      scope: '',
    }),
  defaultTemplate: { subject: '', content: '', footer: martinusMailFooter() },
});

/**
 * Some entrypoint can give the freemail additional information
 */
@Entity()
export class FreeMailAdditionalInformation extends AdornisEntity {
  static override _class = 'FreeMailAdditionalInformation';

  constructor(doc: PartialEntityData<FreeMailAdditionalInformation>) {
    super(doc);
  }

  /**
   * To send references, the group members list passes its filter here. The bookings it selects are
   * resolved on the server and matched to the user or his children - their ids would exceed BaseQL's
   * request cap for any real group.
   */
  @Field(() => AdornisFilter)
  membersFilter: Maybe<AdornisFilter>;
}

/**
 * The ids of the bookings selected by the members filter, null when no filter was given.
 */
async function getBookingIDsForMembersFilter(membersFilter: Maybe<AdornisFilter>): Promise<string[] | null> {
  if (!membersFilter) return null;

  const rows = await firstValueFrom(
    resolveSubscribeGroupParticipantRowPaginatedFiltered(0, -1, membersFilter, {
      booking: { _id: 1, _class: 1 },
      user: { _id: 1, _class: 1 },
      group: { _id: 1, _class: 1 },
    }),
  );
  if (typeof rows === 'number') throw new Error('Expected array of group participant rows, got count');

  return rows.map(row => row.booking._id);
}

async function getBookingForUser(
  user: NarrowedEntity<
    Person,
    {
      _id: 1;
      profilDaten: {
        stammdaten: { geschlecht: 1; titel: 1; vorname: 1; nachname: 1; geburtsDatum: 1 };
        adresse: { streetAndNumber: 1; zip: 1; city: 1; country: 1 };
      };
      sorgeberechtigteKinderIDs: 1;
      email: 1;
    }
  >,
  possibleBookingIDs: Maybe<string[]>,
): Promise<EntityData<Booking> | null> {
  if (!possibleBookingIDs) return null;

  const bookingCol = await getRawCollection<EntityData<Booking>>(Booking._collectionName);

  const booking = await bookingCol.findOne(
    { _id: { $in: possibleBookingIDs }, userID: { $in: [user._id, ...(user.sorgeberechtigteKinderIDs ?? [])] } },
    { projection: { _id: 1 } },
  );

  if (!booking) return null;

  const bookingResolved = await getByID(
    Booking,
    booking._id,
  )({
    bookingReference: 1,
    groupIDs: 1,
    userID: 1,
    groups: { school: { kostenstellePrefix: 1 }, kostenstelle: { kostenstelleSuffix: 1 }, kostenstelleSuffix: 1 },
  });

  return bookingResolved as Booking;
}

async function getPersonForBooking(booking: EntityData<Booking> | null): Promise<EntityData<Person> | null> {
  if (!booking) return null;
  const col = await getRawCollection<EntityData<Person>>(Person._collectionName);

  return col.findOne({ _id: booking.userID });
}

/**
 * The recipients of a list selection: the selected persons, or - when asked - the Sorgeberechtigte
 * of those that have any, the person itself otherwise. The client decides, the server resolves: a
 * recipient id list would not fit into a BaseQL request.
 */
async function getRecipientUserIDs(
  listSelection: UserListSelectionInterface,
  sendToSorgeberechtigte: boolean,
): Promise<string[]> {
  const persons = await resolveGetUsersByListSelection(listSelection)({
    _id: 1,
    sorgeberechtigteEltern: { _id: 1 },
  });

  return Array.from(
    new Set(
      persons.flatMap(person =>
        sendToSorgeberechtigte && person.sorgeberechtigteEltern?.length
          ? person.sorgeberechtigteEltern.map(sbs => sbs._id)
          : [person._id],
      ),
    ),
  );
}

const sendFreeTextMailsToSelection =
  (
    listSelection: UserListSelectionInterface,
    sendToSorgeberechtigte: boolean,
    mailTemplate: MailTemplate,
    additionalInformation?: FreeMailAdditionalInformation,
  ) =>
  () => {
    let i = 0;
    let recipientCount = 0;
    let bookingIDs: string[] | null = null;

    let hasSentToCurrentUser = false;

    return from(
      (async () => {
        const userIDs = await getRecipientUserIDs(listSelection, sendToSorgeberechtigte);
        recipientCount = userIDs.length;
        bookingIDs = await getBookingIDsForMembersFilter(additionalInformation?.membersFilter);

        let fullPersons = await getByID(
          Person,
          userIDs,
        )({
          _id: 1,
          debitorennummer: 1,
          profilDaten: {
            stammdaten: { geschlecht: 1, titel: 1, vorname: 1, nachname: 1, geburtsDatum: 1 },
            adresse: { streetAndNumber: 1, zip: 1, city: 1, country: 1 },
          },
          sorgeberechtigteKinderIDs: 1,
          email: 1,
        });

        /**
         * Filter out placeholders and no.mails
         */
        fullPersons = fullPersons.filter(
          person => !person.email.includes('@placeholder.tmp') && !person.email.includes('@no.mail'),
        );

        return fullPersons.reduce<
          NarrowedEntity<
            Person,
            {
              _id: 1;
              debitorennummer: 1;
              profilDaten: {
                stammdaten: { geschlecht: 1; titel: 1; vorname: 1; nachname: 1; geburtsDatum: 1 };
                adresse: { streetAndNumber: 1; zip: 1; city: 1; country: 1 };
              };
              sorgeberechtigteKinderIDs: 1;
              email: 1;
            }
          >[][]
        >(
          (acc, curr) => {
            const lastEntry = acc[acc.length - 1];
            if (lastEntry && lastEntry.length < 10) lastEntry.push(curr);
            else acc.push([curr]);
            return acc;
          },
          [[]],
        );
      })(),
    ).pipe(
      switchMap(groupedPersons => of(...groupedPersons)),
      concatMap(async persons => {
        const currentUser = await getMyself<Person>()({
          _id: 1,
          email: 1,
          roles: { name: 1, contexts: 1 },
        });
        if (!currentUser) throw new Error('Current user not found');

        const schoolID = (await getSchoolIDsForRoles(currentUser, ACTIVE_ROLES)).at(0);

        if (!hasSentToCurrentUser) {
          hasSentToCurrentUser = true;

          const { subject: subjectSelf, content: contentSelf } = assembleMailTemplate({
            template: mailTemplate,
            infos: {
              [FreeTextMailVariables.TITEL]: '{{titel}}',
              [FreeTextMailVariables.VORNAME]: '{{vorname}}',
              [FreeTextMailVariables.NACHNAME]: '{{nachname}}',
              [FreeTextMailVariables.EMAIL]: currentUser.email,
              [FreeTextMailVariables.GEBURTSDATUM]: '{{geburtsdatum}}',
              [FreeTextMailVariables.GESCHLECHT]: '{{geschlecht}}',
              [FreeTextMailVariables.STRASSE]: '{{strasse}}',
              [FreeTextMailVariables.PLZ]: '{{plz}}',
              [FreeTextMailVariables.ORT]: '{{ort}}',
              [FreeTextMailVariables.LAND]: '{{land}}',
              [FreeTextMailVariables.SET_PASSWORD_LINK]: `<a href="#"> Dummy Link </a>`,
              [FreeTextMailVariables.BUCHUNGS_REFERENZ]: '{{Ueberweiser_Verwendungszweck}}',
              [FreeTextMailVariables.PORTAL_LINK]: `<a href="${A.absoluteUrl()}"> ${A.absoluteUrl()} </a>`,
            },
            // only render the substituted body — makeMartinusEmailTemplate below is the
            // single email wrapper and resolves schoolID into the branded footer/logo
            renderFunction: options => html`${options.contents}`,
          });

          /**
           * Send it to the curretn user himself
           */
          const message = {
            subject: 'Freitext E-Mail versendet mit dem Betreff: ' + (subjectSelf || '*No subject defined*'),
            html: await makeMartinusEmailTemplate(subjectSelf || '*No subject defined*', [contentSelf], schoolID),
          };

          await getMartinusMailer().send({
            message,
            recipients: new Recipient({ to: currentUser.email, replyTo: 'info@sg-sankt-martinus.de' }),
          });
        }

        /**
         * All persons
         */
        for (const person of persons) {
          const resetLink = await getPasswordResetLink(SGRoutes.setPassword(), {
            userClass: Person._class,
            userID: person._id,
            ttu: 1,
            ttl: 1000 * 60 * 60 * 24 * 7,
          });

          const booking = await getBookingForUser(person, bookingIDs);
          const bookingPerson = await getPersonForBooking(booking);
          const school = (booking?.groups?.at(0) as SchoolSubGroup | undefined)?.school;
          const kostenstelleSuffix =
            (booking?.groups?.at(0) as KostenstelleSubGroup | undefined)?.kostenstelle?.kostenstelleSuffix ??
            (booking?.groups?.at(0) as Kostenstelle | undefined)?.kostenstelleSuffix;
          const reference = `"${booking?.bookingReference} ${bookingPerson?.debitorennummer ?? ''} ${school?.kostenstellePrefix ?? ''}${kostenstelleSuffix ?? ''} ${bookingPerson?.profilDaten?.stammdaten?.vorname ?? ''} ${bookingPerson?.profilDaten?.stammdaten?.nachname ?? ''}"`;

          const { subject, content } = assembleMailTemplate({
            template: mailTemplate,
            infos: {
              [FreeTextMailVariables.TITEL]: person.profilDaten?.stammdaten?.titel ?? '',
              [FreeTextMailVariables.VORNAME]: person.profilDaten?.stammdaten?.vorname ?? '',
              [FreeTextMailVariables.NACHNAME]: person.profilDaten?.stammdaten?.nachname ?? '',
              [FreeTextMailVariables.EMAIL]: person.email,
              [FreeTextMailVariables.GEBURTSDATUM]: person.profilDaten?.stammdaten?.geburtsDatum
                ? formatCalendarDay(person.profilDaten.stammdaten.geburtsDatum, 'dd.MM.yyyy')
                : '',
              [FreeTextMailVariables.GESCHLECHT]: person.profilDaten?.stammdaten?.geschlecht ?? '',
              [FreeTextMailVariables.STRASSE]: person.profilDaten?.adresse?.streetAndNumber ?? '',
              [FreeTextMailVariables.PLZ]: person.profilDaten?.adresse?.zip ?? '',
              [FreeTextMailVariables.ORT]: person.profilDaten?.adresse?.city ?? '',
              [FreeTextMailVariables.LAND]: person.profilDaten?.adresse?.country ?? '',
              [FreeTextMailVariables.SET_PASSWORD_LINK]: `<a href="${resetLink}"> ${resetLink} </a>`,
              [FreeTextMailVariables.BUCHUNGS_REFERENZ]: reference,
              [FreeTextMailVariables.PORTAL_LINK]: `<a href="${A.absoluteUrl()}"> ${A.absoluteUrl()} </a>`,
            },
            // only render the substituted body — makeMartinusEmailTemplate below is the
            // single email wrapper and resolves schoolID into the branded footer/logo
            renderFunction: options => html`${options.contents}`,
          });

          const message = {
            subject: subject || '*No subject defined*',
            html: await makeMartinusEmailTemplate(subject || '*No subject defined*', [content], schoolID),
          };

          await getMartinusMailer().send({
            message,
            recipients: new Recipient({ to: person.email, replyTo: 'info@sg-sankt-martinus.de' }),
          });

          i++;
        }

        return i;
      }),
      map(i => `${i} von ${recipientCount} Mails versandt...`),
    );
  };

export const sendFreeTextMail = registerSubscription({
  operationName: 'sendFreeTextMail',
  type: () => String,
  params: [
    { name: 'listSelection', type: () => UserListSelectionInterface },
    { name: 'sendToSorgeberechtigte', type: () => Boolean },
    { name: 'mailTemplate', type: () => MailTemplate },
    { name: 'additionalInformation', type: () => FreeMailAdditionalInformation },
  ],
  middlewares: [checkPermissionMiddleware(Permission.EMAIL_SENDEN)],
  resolve: sendFreeTextMailsToSelection,
});
